import * as React from 'react'
import { Autocomplete, Box, Button, Grid, Stack, TextField } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'

import { SectionCard } from './SectionCard'
import type {
	UpdateWorkActivityRequest,
	WorkActivityRegistryModel,
} from '../../types/Work/WorkEntry'
import { BillingLogSelect } from './BillingLogSelect'
import { Spec } from '../../types/Project/Spec'
import { getProjectSpecs } from '../../services/projectService'
import { getBillingLogEntries } from '../../services/billingLogService'
import { createWorkActivity } from '../../services/WSRService'
import type { BillingLogEntryResponse } from '../../types/Contracts/BillingLog'
import { showSuccessToast, showErrorToast } from '../../services/CommonServices/alertServices'

function parseRegistryDate(iso: string | null): Dayjs | null {
	if (!iso || !iso.trim()) return null
	const d = dayjs(iso)
	return d.isValid() ? d : null
}

type Props = {
	value: WorkActivityRegistryModel
	onChange: (patch: Partial<WorkActivityRegistryModel>) => void
	onSubmitNewWorkActivity: () => void
	loadedTagId: number | null
	tagNumber: number | null
	activityRecords: UpdateWorkActivityRequest[]
	setDefaultActivityForm: () => void
	projectNumber?: string
}

export function WorkActivityRegistryForm({
	value,
	onChange,
	onSubmitNewWorkActivity,
	loadedTagId,
	tagNumber,
	activityRecords,
	setDefaultActivityForm,
	projectNumber,
}: Props) {
	const [activityTypes, setActivityTypes] = React.useState<Spec[]>([])
	const [billingLogEntries, setBillingLogEntries] = React.useState<BillingLogEntryResponse[]>([])
	const [billingLogEntriesLoading, setBillingLogEntriesLoading] = React.useState(true)
	const [isSubmitting, setIsSubmitting] = React.useState(false)

	const projectNumberStr = projectNumber ?? ''

	React.useEffect(() => {
		let cancelled = false

		const loadData = async () => {
			setBillingLogEntriesLoading(true)
			try {
				const [specs, logs] = await Promise.all([getProjectSpecs(true), getBillingLogEntries()])
				if (!cancelled) {
					setActivityTypes(specs)
					setBillingLogEntries(logs)
				}
			} catch {
				if (!cancelled) {
					setActivityTypes([])
					setBillingLogEntries([])
				}
			} finally {
				if (!cancelled) setBillingLogEntriesLoading(false)
			}
		}

		void loadData()
		return () => {
			cancelled = true
		}
	}, [])

	const handleCreateWorkActivity = async () => {
		if (loadedTagId === null) {
			showErrorToast({ message: 'Tag number is required' })
			return
		}
		setIsSubmitting(true)
		const plannedHrs =
			value.plannedHrs && value.plannedHrs.trim() !== '' ? Number(value.plannedHrs) : null
		const percentComplete =
			value.percentComplete && value.percentComplete.trim() !== ''
				? Number(value.percentComplete)
				: null

		try {
			const success = await createWorkActivity({
				scaffoldTagId: loadedTagId,
				specId: value.specId,
				activityType: value.activityType,
				warIdSummary: value.warIdSummary,
				requestDate:
					value.requestDate && value.requestDate.trim() !== '' ? value.requestDate : null,
				plannedDate:
					value.plannedDate && value.plannedDate.trim() !== '' ? value.plannedDate : null,
				plannedHrs: plannedHrs !== null && Number.isFinite(plannedHrs) ? plannedHrs : null,
				percentComplete:
					percentComplete !== null && Number.isFinite(percentComplete) ? percentComplete : null,
				details: value.details,
				wo: value.wo.trim() !== '' ? value.wo.trim() : null,
				billingLogEntryId: value.billingLogEntryId,
			})
			onSubmitNewWorkActivity()
			showSuccessToast('Work activity created successfully', true)
			setDefaultActivityForm()
		} finally {
			setIsSubmitting(false)
		}
	}
	return (
		<SectionCard title='Work Activity Registry Info'>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
				<Grid container spacing={1}>
					<Grid item xs={12} md={3}>
						<Autocomplete
							disabled={loadedTagId === null}
							options={activityTypes}
							value={activityTypes.find((x) => String(x.id) === value.activityType) ?? null}
							onChange={(_, newValue: Spec | null) => {
								// count the number activity records with the same specId
								const count = activityRecords.filter((x) => x.specId === newValue?.id).length
								onChange({
									activityType: newValue ? String(newValue.id) : '',
									specId: newValue ? newValue.id : 0,
									// add last three characters project number at the beginning of the warIdSummary
									warIdSummary: `${projectNumberStr.slice(-3)}.${tagNumber}.${
										parseInt(newValue?.tasks[0].activityCode ?? '0') + count
									}`,
								})
							}}
							getOptionLabel={(option) => option.name}
							isOptionEqualToValue={(option, selected) => option.id === selected.id}
							renderInput={(params) => (
								<TextField
									{...params}
									label='Activity Type'
									size='small'
									fullWidth
									sx={{
										'& .MuiInputBase-root.Mui-disabled': {
											bgcolor: 'action.disabledBackground',
										},
										'& .MuiInputBase-input.Mui-disabled': {
											color: 'text.primary',
											WebkitTextFillColor: 'inherit',
											opacity: 1,
										},
									}}
								/>
							)}
							fullWidth
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField
							label='WAR ID (Summary #)'
							size='small'
							value={value.warIdSummary}
							onChange={(e) => onChange({ warIdSummary: e.target.value })}
							fullWidth
							disabled
							sx={{
								'& .MuiInputBase-root.Mui-disabled': {
									bgcolor: 'action.disabledBackground',
								},
								'& .MuiInputBase-input.Mui-disabled': {
									color: 'text.primary',
									WebkitTextFillColor: 'inherit',
									opacity: 1,
								},
							}}
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField
							label='Work Order'
							size='small'
							value={value.wo}
							onChange={(e) => onChange({ wo: e.target.value })}
							inputProps={{ maxLength: 500 }}
							fullWidth
							disabled={loadedTagId === null}
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<BillingLogSelect
							value={value.billingLogEntryId}
							onChange={(billingLogEntryId) => onChange({ billingLogEntryId })}
							logs={billingLogEntries}
							loading={billingLogEntriesLoading}
							disabled={loadedTagId === null}
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<DatePicker
							label='Request Date'
							inputFormat='DD/MM/YYYY'
							value={parseRegistryDate(value.requestDate)}
							onChange={(v: Dayjs | null) =>
								onChange({
									requestDate: v && v.isValid() ? v.format('YYYY-MM-DD') : null,
								})
							}
							renderInput={(params) => <TextField {...params} size='small' fullWidth />}
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<DatePicker
							label='Planned Date'
							inputFormat='DD/MM/YYYY'
							value={parseRegistryDate(value.plannedDate)}
							onChange={(v: Dayjs | null) =>
								onChange({
									plannedDate: v && v.isValid() ? v.format('YYYY-MM-DD') : null,
								})
							}
							renderInput={(params) => <TextField {...params} size='small' fullWidth />}
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField
							label='Planned Hrs'
							size='small'
							type='number'
							inputProps={{ min: 0 }}
							value={value.plannedHrs}
							onChange={(e) => onChange({ plannedHrs: e.target.value })}
							fullWidth
							sx={{
								backgroundColor:
									value.plannedHrs && value.plannedHrs.trim() !== '' && Number(value.plannedHrs) < 0
										? 'rgba(255,230,222,1)'
										: 'inherit',
							}}
						/>
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField
							type='number'
							inputProps={{ min: 0, max: 100 }}
							label='% Comp'
							size='small'
							value={value.percentComplete}
							onChange={(e) => onChange({ percentComplete: e.target.value })}
							sx={{
								backgroundColor:
									value.percentComplete &&
									value.percentComplete.trim() !== '' &&
									(Number(value.percentComplete) < 0 || Number(value.percentComplete) > 100)
										? 'rgba(255,230,222,1)'
										: 'inherit',
							}}
							fullWidth
						/>
					</Grid>
				</Grid>

				<TextField
					label='Details'
					size='small'
					value={value.details}
					onChange={(e) => onChange({ details: e.target.value })}
					fullWidth
					multiline
					minRows={2}
				/>

				<Stack direction='row' spacing={1} justifyContent='flex-end' flexWrap='wrap'>
					<Button
						variant='outlined'
						color='primary'
						disabled={value.warIdSummary === ''}
						sx={{ color: 'primary.main', '&:hover': { color: 'primary.main' } }}
					>
						UPDATE REQUEST
					</Button>
					<Button
						variant='contained'
						disabled={value.warIdSummary === '' || isSubmitting}
						color='success'
						onClick={handleCreateWorkActivity}
					>
						{isSubmitting ? 'SUBMITTING...' : 'SUBMIT NEW REQUEST'}
					</Button>
				</Stack>
			</Box>
		</SectionCard>
	)
}
